import { MsalContext } from '../Contexts/MsalContext';
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import React, { useContext, useEffect, useState } from "react";
import ClientReportViewModel from "../PowerBIReport/ViewModels/FlagstoneClientReportModel";
import Button from "@mui/material/Button";
import isValidGUID from '../Utilities/GuidValidator';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { ValidatorForm, SelectValidator, TextValidator } from 'react-material-ui-form-validator';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';

interface IClientReportDialogProps {
    clientReportModel: ClientReportViewModel | null;
    onClose: () => void;
    open: boolean;
}

const styles = {
    typographyGrid: {
        marginTop: "7px",
        alignContent: 'space-evenly'
    },
    textFieldGrid: {
        marginTop: "7px",
    }
}

export default function AddEditClientReportDialog({ clientReportModel, onClose, open }: IClientReportDialogProps) {
    const { msalFetch } = useContext(MsalContext);
    const [clientName, setClientName] = useState("");
    const [clientList, setClientList] = useState<string[]>([]);
    const [todayReport, setTodayReport] = useState("");
    const [historicReport, setHistoricReport] = useState("");
    const [clientReportToday, setClientReportToday] = useState("");
    const [clientReportHistoric, setClientReportHistoric] = useState("");
    const [internalLink, setInternalLink] = useState("");
    const [externalLink, setExternalLink] = useState("");

    useEffect(() => {
        if (open) {
            getClients(); //need to check the list incase a report was deleted/added to prevent duplicates

            if (clientReportModel === null) {
                //clear the data has to be done on open incase a user trys to add 2+ clients at once (if clientReportModel === null * 2 the data wouldn't clear)
                setClientName("");
                setTodayReport("");
                setHistoricReport("");
                setClientReportToday("");
                setClientReportHistoric("");
                setInternalLink("");
                setExternalLink("");
            }
        }
    }, [open]);

    useEffect(() => {
        //assign provided values if possible
        if (clientReportModel !== null) {
            setClientName(clientReportModel.client);
            setTodayReport(clientReportModel.todayGuid ?? "");
            setHistoricReport(clientReportModel.historicGuid ?? "");
            setClientReportToday(clientReportModel.clientTodayGuid ?? "");
            setClientReportHistoric(clientReportModel.clientHistoricGuid ?? "");
            setInternalLink(clientReportModel.internalOtherLinksGuid ?? "");
            setExternalLink(clientReportModel.externalOtherLinksGuid ?? "");
        }
    }, [clientReportModel]);

    function getClients() {
        msalFetch('api/ClientReport/GetClientWithNoReportsList')
            .then(response => response.json())
            .then(data => {
                setClientList(data);
            });
    }

    //save / update data 
    function AddEditClientReport(data: ClientReportViewModel) {
        msalFetch('api/ClientReport/AddUpdateClientReport/', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json; charset=utf-8',
            },
            body: JSON.stringify(data)
        })
            .then(response => response.json())
            .then((data: boolean) => {
                if (data)
                    onClose();
            });
    }

    function ExtractGuidFromStringOrHyperlink(http: string): string {
        let findGuid: string[] = http.split('/'); //guid validation is done in the validation form not here
        if (findGuid.length >= 6) //a valid hyperlink will ahve the guid in the 6th position
            return findGuid[6];
        else
            return http;
    }

    ValidatorForm.addValidationRule('guidRegex', (value) => {
        return isValidGUID(value);
    });

    ValidatorForm.addValidationRule('emptyOrGuidRegex', (value) => {
        return value === "" || isValidGUID(value);
    });

    function Confirm() {
        //create a new mode for the new path and use the modified model for the upate path
        var saveValue: ClientReportViewModel = clientReportModel === null ? new ClientReportViewModel() : clientReportModel;

        //add the new values to the model
        saveValue.client = clientName;
        saveValue.todayGuid = todayReport;
        saveValue.historicGuid = historicReport;
        saveValue.clientTodayGuid = clientReportToday;
        saveValue.clientHistoricGuid = clientReportHistoric;
        saveValue.internalOtherLinksGuid = internalLink;
        saveValue.externalOtherLinksGuid = externalLink;

        AddEditClientReport(saveValue);
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md">
            <DialogContent>
                <ValidatorForm onSubmit={Confirm}>
                    <Grid container>
                        <Grid size={12}>
                            <Typography variant="h5">{clientReportModel !== null ? "Update Client" : "New Client"}</Typography>
                        </Grid>
                        <Grid size={12}>
                            <Typography style={{ marginTop: '3px', marginBottom: '3px' }}>Valid Hyperlinks Can Be Pasted Into Today/Historic And They Will Automatically Formatted</Typography>
                        </Grid>
                        <Grid sx={styles.typographyGrid} size={3}>
                            <Typography>Client : </Typography>
                        </Grid>
                        <Grid sx={styles.textFieldGrid} size={9}>
                            {clientReportModel === null ?
                                <SelectValidator
                                    fullWidth
                                    value={clientName}
                                    validators={['required']}
                                    errorMessages={['Selected Client Is Invalid']}
                                    onChange={(event) => {
                                        setClientName(event.target.value as string);
                                    }}>
                                    {clientList.map((item) =>
                                        <MenuItem value={item}>{item}</MenuItem>
                                    )}
                                </SelectValidator>
                                :
                                <TextField fullWidth disabled={true} value={clientName} />
                            }
                        </Grid>
                        <Grid sx={styles.typographyGrid} size={3}>
                            <Typography>Ops Today's Report ID : </Typography>
                        </Grid>
                        <Grid sx={styles.textFieldGrid} size={9}>
                            <TextValidator
                                fullWidth
                                label="Set Ops Today's Report"
                                value={todayReport}
                                validators={['required', 'guidRegex']}
                                errorMessages={['Ops Today\'s Report Guid Is Invalid']}
                                onChange={(event) => {
                                    setTodayReport(ExtractGuidFromStringOrHyperlink(event.target.value));
                                }} />
                        </Grid>
                        <Grid sx={styles.typographyGrid} size={3}>
                            <Typography>Ops Historic Report ID : </Typography>
                        </Grid>
                        <Grid sx={styles.textFieldGrid} size={9}>
                            <TextValidator
                                fullWidth
                                label="Set Ops Historic Report"
                                value={historicReport}
                                validators={['required', 'guidRegex']}
                                errorMessages={['Ops Historic Report Guid Is Invalid']}
                                onChange={(event) => {
                                    setHistoricReport(ExtractGuidFromStringOrHyperlink(event.target.value));
                                }} />
                        </Grid>
                        <Grid sx={styles.typographyGrid} size={3}>
                            <Typography>Client Today Report ID : </Typography>
                        </Grid>
                        <Grid sx={styles.textFieldGrid} size={9}>
                            <TextValidator
                                fullWidth
                                label="Set Client Today Report"
                                value={clientReportToday}
                                validators={['emptyOrGuidRegex']}
                                errorMessages={['Client Today Report Guid Is Invalid']}
                                onChange={(event) => {
                                    setClientReportToday(ExtractGuidFromStringOrHyperlink(event.target.value));
                                }} />
                        </Grid>
                        <Grid sx={styles.typographyGrid} size={3}>
                            <Typography>Client Historic Report ID : </Typography>
                        </Grid>
                        <Grid sx={styles.textFieldGrid} size={9}>
                            <TextValidator
                                fullWidth
                                label="Set Client Historic Report"
                                value={clientReportHistoric}
                                validators={['emptyOrGuidRegex']}
                                errorMessages={['Client Historic Report Guid Is Invalid']}
                                onChange={(event) => {
                                    setClientReportHistoric(ExtractGuidFromStringOrHyperlink(event.target.value));
                                }} />
                        </Grid>
                        <Grid sx={styles.typographyGrid} size={3}>
                            <Typography>Other Internal Link ID : </Typography>
                        </Grid>
                        <Grid sx={styles.textFieldGrid} size={9}>
                            <TextValidator
                                fullWidth
                                label="Set Other Internal Link"
                                value={internalLink}
                                validators={['emptyOrGuidRegex']}
                                errorMessages={['Other Internal Link Is Invalid']}
                                onChange={(event) => {
                                    setInternalLink(ExtractGuidFromStringOrHyperlink(event.target.value));
                                }} />
                        </Grid>
                        <Grid sx={styles.typographyGrid} size={3}>
                            <Typography>Other External Link ID : </Typography>
                        </Grid>
                        <Grid sx={styles.textFieldGrid} size={9}>
                            <TextValidator
                                fullWidth
                                label="Set Other External Link"
                                value={externalLink}
                                validators={['emptyOrGuidRegex']}
                                errorMessages={['Other External Link Guid Is Invalid']}
                                onChange={(event) => {
                                    setExternalLink(ExtractGuidFromStringOrHyperlink(event.target.value));
                                }} />
                        </Grid>
                    </Grid>
                    <DialogActions>
                        <Button onClick={() => onClose()}>Cancel</Button>
                        <Button type="submit">Save</Button>
                    </DialogActions>
                </ValidatorForm>
            </DialogContent>
        </Dialog >
    );
}